import AdminAssignmentsService from '../api/AdminAssignmentsService';
import type { AssignmentActionInput } from '@/client/services/api/graphql/gql/graphql';
import { useMutation } from '@tanstack/react-query';
import { useToastStore } from '@/client/services/state/toastStore';
import { useTranslation } from 'react-i18next';

export const useWorkflowActions = () => {
  const { t } = useTranslation();
  const { setToast } = useToastStore();

  const archiveAssignmentsMutation = useMutation({
    mutationFn: (payload: AssignmentActionInput) =>
      AdminAssignmentsService.archiveAssignments(payload),
    onSuccess: () =>
      setToast({
        show: true,
        status: 'success',
        title: t('admin.assignments.workflowAction.archive.success'),
      }),
    onError: (err) => {
      console.error('Error archiving assignments: ', err);

      setToast({
        show: true,
        status: 'error',
        title: t('admin.assignments.workflowAction.archive.error'),
      });
    },
  });

  const markAssignmentsCompletedMutation = useMutation({
    mutationFn: (payload: AssignmentActionInput) =>
      AdminAssignmentsService.markAssignmentsCompleted(payload),
    onSuccess: () =>
      setToast({
        show: true,
        status: 'success',
        title: t('admin.assignments.workflowAction.complete.success'),
      }),
    onError: (err) => {
      console.error('Error completing assignments: ', err);

      setToast({
        show: true,
        status: 'error',
        title: t('admin.assignments.workflowAction.complete.error'),
      });
    },
  });

  const sendAssignmentsReminderMutation = useMutation({
    mutationFn: (payload: AssignmentActionInput) =>
      AdminAssignmentsService.sendAssignmentsReminder(payload),
    onSuccess: () =>
      setToast({
        show: true,
        status: 'success',
        title: t('admin.assignments.workflowAction.remind.success'),
      }),
    onError: (err) => {
      console.error('Error sending assignment reminders: ', err);

      setToast({
        show: true,
        status: 'error',
        title: t('admin.assignments.workflowAction.remind.error'),
      });
    },
  });

  interface DeactivateOrActivateWorkflowMutationVariables {
    workflowId: string;
    refetchAction: () => void;
  }

  const deactivateWorkflowMutation = useMutation({
    mutationFn: ({ workflowId }: DeactivateOrActivateWorkflowMutationVariables) =>
      AdminAssignmentsService.deactivateWorkflow(workflowId),
    onSuccess: (_, { refetchAction }) => {
      refetchAction();

      setToast({
        show: true,
        status: 'success',
        title: t('admin.assignments.details.actions.deactivate.success'),
      });
    },
    onError: (err) => {
      console.error('Error deactivating workflow: ', err);

      setToast({
        show: true,
        status: 'error',
        title: t('admin.assignments.details.actions.deactivate.error'),
      });
    },
  });

  const activateWorkflowMutation = useMutation({
    mutationFn: ({ workflowId }: DeactivateOrActivateWorkflowMutationVariables) =>
      AdminAssignmentsService.activateWorkflow(workflowId),
    onSuccess: (_, { refetchAction }) => {
      refetchAction();

      setToast({
        show: true,
        status: 'success',
        title: t('admin.assignments.details.actions.activate.success'),
      });
    },
    onError: (err) => {
      console.error('Error activating workflow: ', err);

      setToast({
        show: true,
        status: 'error',
        title: t('admin.assignments.details.actions.activate.error'),
      });
    },
  });

  return {
    archiveAssignmentsMutation,
    markAssignmentsCompletedMutation,
    sendAssignmentsReminderMutation,
    deactivateWorkflowMutation,
    activateWorkflowMutation,
  };
};
